import { FeedbackSheetHost } from '@/features/feedback/capture/FeedbackSheetHost';
import { repositoryFileRoutePaths } from '@/features/repository-files/repositoryFileRoute';
import { APP_HOME_PATH } from '@/routing/appHome';
import { appPath } from '@featit/shared/addressing/paths';
import { useEffect, type ReactElement } from 'react';
import {
  createBrowserRouter,
  matchRoutes,
  Outlet,
  RouterProvider,
  type RouteObject,
} from 'react-router-dom';
import { StoreProvider } from '@/providers/StoreProvider';
import { RefusalReasons } from '@/components/RefusalReasons';
import { RefusedTouchHint } from '@/components/RefusedTouchHintChunk';
import { GatePendingStudiesOnDemand } from '@/components/waiting/GatePendingStudiesOnDemand';
import { DeploymentUpdateProvider } from '@/components/DeploymentUpdate';
import { subscribeToReaderNavigation } from '@/routing/readerNavigation';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { bootDataOf, holdForBootRoutes, type IBootDataHandle } from '@/routing/bootRoutes';
import { decisionApi } from '@/services/DecisionApi';
import { Layout } from '@/components/Layout';
import { preloadSidebarViews } from '@/features/views/ViewNavigationListChunk';
import { LoginPage } from '@/features/auth/LoginPage';
import { PublicFrame, PublicLayout } from '@/components/PublicLayout';
import { EmailVerificationBanner } from '@/features/auth/EmailVerificationBanner';
import { MailStandingBanner } from '@/features/auth/MailStandingBanner';
import { PendingAccountDeletionBanner } from '@/features/auth/PendingAccountDeletionBanner';
import { ConnectDeploymentPage } from '@/features/onboarding/ConnectDeploymentPage';
import { WelcomePage } from '@/features/onboarding/WelcomePage';
import { useDeploymentReload } from '@/hooks/useDeploymentReload';
import { UnsavedChangesProvider } from '@/features/settings/UnsavedChangesProvider';
import { DesktopTitleBarFallback, DesktopTitleBarProvider } from '@/desktop/DesktopTitleBarSlot';
import { useMachineReporter } from '@/features/infrastructure/useMachineReporter';
import { useBrowserWatchReporter } from '@/features/infrastructure/useBrowserWatchReporter';
import { useDesktopReturnLinks } from '@/desktop/useDesktopReturnLinks';
import { useDesktopMenuRoutes } from '@/desktop/desktopMenuRoute';
import { installCreateKeyboardPrime } from '@/shell/createKeyboardPrime';
import { WorkAreaRouteBoundary } from '@/routing/WorkAreaRouteBoundary';
import {
  appRoutes,
  legacyAddressRoutes,
  namespaceDecisionRoutes,
  namespaceTaskRoutes,
  WORK_AREA_BOUNDARY_HANDLE,
  workAreaAddressRoutes,
  activityItemRoutes,
  workAreasDecisionRoutes,
  workAreasTaskRoutes,
} from '@/routing/addressRoutes';
import { LegacyAddressRedirect } from '@/routing/LegacyAddressRedirect';
import { RouteRedirect } from '@/routing/RouteRedirect';
import { HoldsAddressRedirect } from '@/features/tasks/HoldsAddressRedirect';
import {
  NAMESPACE_ACTIVITY_SEGMENT,
  NAMESPACE_DECISIONS_SEGMENT,
  NAMESPACE_ITEMS_SEGMENT,
  NAMESPACE_TASKS_SEGMENT,
  NAMESPACE_VIEWS_SEGMENT,
} from '@/features/work-areas/workAreaOverviewUrls';
import { NamespaceAddressPage } from '@/routing/NamespaceAddressPage';
import { MaintenanceNotice } from '@/maintenance/MaintenanceNotice';
import { UngatedPreviewBar } from '@/components/UngatedPreviewBar';
import { AppViewport } from '@/components/AppViewport';
import { lazyPage } from '@/routing/lazyRoute';
import { RouteLoadError } from '@/routing/RouteLoadError';
import { ShellRuntime } from '@/shell/loadShellRuntime';
import { MacAddressConsentPrompt } from '@/features/settings/MacAddressConsentPromptChunk';
import { InternalLinkClicks } from '@/routing/InternalLinkClicks';
import { HistoryEntries } from '@/shell/historyBackLabel';
import { ShellSpareScreen } from '@/shell/ShellSpareScreen';
import { useSinglePageScroller } from '@/shell/useSinglePageScroller';
import { ViewsIndexRoute } from '@/features/views/ViewsIndexRoute';

/*
 * The artifact routes' availability gate loads with them: the launch entry carries none of its
 * `artifactSharing` words, which only these three routes draw.
 */
const ArtifactSharingAvailabilityBoundary = lazyPage(
  () => import('@/features/artifact-sharing/ArtifactSharingAvailability'),
  (module) => module.ArtifactSharingAvailabilityBoundary,
);
const ArtifactSnapshotConfirmationPage = lazyPage(
  () => import('@/features/artifact-sharing/ArtifactSnapshotConfirmationPage'),
  (module) => module.ArtifactSnapshotConfirmationPage,
);
const ArtifactRecipientPage = lazyPage(
  () => import('@/features/artifact-sharing/ArtifactRecipientPage'),
  (module) => module.ArtifactRecipientPage,
);
const ArtifactInvitationPage = lazyPage(
  () => import('@/features/artifact-sharing/ArtifactRecipientPage'),
  (module) => module.ArtifactInvitationPage,
);

// T-0770: the phone's More tab is drawn on one route, so its screen (and the page header it alone carries)
// loads with that route; the signed-in routes' own wait state covers the load.
const MoreScreen = lazyPage(
  () => import('@/shell/MoreScreen'),
  (module) => module.MoreScreen,
);
const FeedbackDetailRoute = lazyPage(
  () => import('@/features/feedback/FeedbackDetailRoute'),
  (module) => module.FeedbackDetailRoute,
);
// F-0774 (ONE-4): a work area's old settings address forwards to Settings' work-area group.
const WorkAreaSettingsRedirect = lazyPage(
  () => import('@/features/settings/settingsWorkAreaGroup'),
  (module) => module.WorkAreaSettingsRedirect,
);
// I-0924: the reporter page draws the team's messages with the markdown viewer, which carries the
// editor; loaded on its own route so no first screen pays for it (check-first-screen-chunks).
/*
 * T-0559: the doors before an account, each its own chunk, so the app's launch does not parse them.
 * `/connect`, `/welcome` and `/login` stay in the entry: they are drawn before any deployment has
 * answered, and a chunk from a deployment that is not answering would never arrive.
 */
const DesktopAuthPage = lazyPage(
  () => import('@/features/auth/DesktopAuthPage'),
  (module) => module.DesktopAuthPage,
);
const CliAuthPage = lazyPage(
  () => import('@/features/auth/CliAuthPage'),
  (module) => module.CliAuthPage,
);
const MobileAuthPage = lazyPage(
  () => import('@/features/auth/MobileAuthPage'),
  (module) => module.MobileAuthPage,
);
const PasskeyCeremonyPage = lazyPage(
  () => import('@/features/auth/PasskeyCeremonyPage'),
  (module) => module.PasskeyCeremonyPage,
);
const AppSetupPage = lazyPage(
  () => import('@/features/app-setup/AppSetupPage'),
  (module) => module.AppSetupPage,
);
const ConsoleSignInPage = lazyPage(
  () => import('@/features/auth/ConsoleSignInPage'),
  (module) => module.ConsoleSignInPage,
);
// I-2219: where FEATit's Claude Desktop extension ends the tab once it holds its token.
const ConnectedPage = lazyPage(
  () => import('@/features/auth/ConnectedPage'),
  (module) => module.ConnectedPage,
);
const ConnectorConsentPage = lazyPage(
  () => import('@/features/auth/ConnectorConsentPage'),
  (module) => module.ConnectorConsentPage,
);
const VerifySessionPage = lazyPage(
  () => import('@/features/auth/VerifySessionPage'),
  (module) => module.VerifySessionPage,
);
// F-0581: Get started, which a first start opens (OB-1).
const GetStartedPage = lazyPage(
  () => import('@/features/get-started/GetStartedPage'),
  (module) => module.GetStartedPage,
);
const RegisterPage = lazyPage(
  () => import('@/features/auth/RegisterPage'),
  (module) => module.RegisterPage,
);
const VerifyEmailPage = lazyPage(
  () => import('@/features/auth/VerifyEmailPage'),
  (module) => module.VerifyEmailPage,
);
const ForgotPasswordPage = lazyPage(
  () => import('@/features/auth/ForgotPasswordPage'),
  (module) => module.ForgotPasswordPage,
);
const ResetPasswordPage = lazyPage(
  () => import('@/features/auth/ResetPasswordPage'),
  (module) => module.ResetPasswordPage,
);
const SetupPage = lazyPage(
  () => import('@/features/auth/SetupPage'),
  (module) => module.SetupPage,
);
const ClaimPage = lazyPage(
  () => import('@/features/auth/ClaimPage'),
  (module) => module.ClaimPage,
);
const UnsubscribePage = lazyPage(
  () => import('@/features/auth/UnsubscribePage'),
  (module) => module.UnsubscribePage,
);
const ConfirmSignInAddressPage = lazyPage(
  () => import('@/features/auth/ConfirmSignInAddressPage'),
  (module) => module.ConfirmSignInAddressPage,
);
const UndoSignInAddressPage = lazyPage(
  () => import('@/features/auth/UndoSignInAddressPage'),
  (module) => module.UndoSignInAddressPage,
);
const BillingReturnRedirect = lazyPage(
  () => import('@/features/billing/BillingReturnRedirect'),
  (module) => module.BillingReturnRedirect,
);
const AccountCallbackPage = lazyPage(
  () => import('@/features/settings/AccountCallbackPage'),
  (module) => module.AccountCallbackPage,
);
const ReporterPage = lazyPage(
  () => import('@/features/reporters/ReporterPage'),
  (module) => module.ReporterPage,
);
const ReviewLinkPage = lazyPage(
  () => import('@/features/review-link/ReviewLinkPage'),
  (module) => module.ReviewLinkPage,
);
const JoinPage = lazyPage(
  () => import('@/features/access/JoinPage'),
  (module) => module.JoinPage,
);
const InvitePage = lazyPage(
  () => import('@/features/access/InvitePage'),
  (module) => module.InvitePage,
);
const CustomerLinkOpenPage = lazyPage(
  () => import('@/features/sharing/CustomerLinkOpenPage'),
  (module) => module.CustomerLinkOpenPage,
);
const OpenRepositoryFilePage = lazyPage(
  () => import('@/features/repository-files/OpenRepositoryFilePage'),
  (module) => module.OpenRepositoryFilePage,
);
const OrganisationSettingsPage = lazyPage(
  () => import('@/features/organisations/OrganisationSettingsPage'),
  (module) => module.OrganisationSettingsPage,
);
const WorkItemsPage = lazyPage(
  () => import('@/features/work-items/WorkItemsPage'),
  (module) => module.WorkItemsPage,
);
const WorkItemCreatePage = lazyPage(
  () => import('@/features/work-items/WorkItemCreatePage'),
  (module) => module.WorkItemCreatePage,
);
const RoadmapPage = lazyPage(
  () => import('@/features/roadmap/RoadmapPage'),
  (module) => module.RoadmapPage,
);
const ActivityPage = lazyPage(
  () => import('@/features/activity/ActivityPage'),
  (module) => module.ActivityPage,
);
// F-0564: a commit opens its diff.
const CommitDiffPage = lazyPage(
  () => import('@/features/commits/CommitDiffPage'),
  (module) => module.CommitDiffPage,
);
const RepositoryFilePage = lazyPage(
  () => import('@/features/repository-files/RepositoryFilePage'),
  (module) => module.RepositoryFilePage,
);
const WorkAreaActivityItemScreen = lazyPage(
  () => import('@/features/activity/WorkAreaActivityItemScreen'),
  (module) => module.WorkAreaActivityItemScreen,
);
const CustomViewsPage = lazyPage(
  () => import('@/features/views/CustomViewsPage'),
  (module) => module.CustomViewsPage,
);
// F-0549: the bare Views address in every scope is the overview (VO-1, VO-17).
const ViewsOverviewPage = lazyPage(
  () => import('@/features/views/ViewsOverviewPage'),
  (module) => module.ViewsOverviewPage,
);
const DashboardPage = lazyPage(
  () => import('@/features/dashboard/DashboardPage'),
  (module) => module.DashboardPage,
);
// AGM-42: the same page, opened for one item; in the page's own chunk.
const ItemScopedDashboardPage = lazyPage(
  () => import('@/features/dashboard/DashboardPage'),
  (module) => module.ItemScopedDashboardPage,
);
const DocumentsPage = lazyPage(
  () => import('@/features/documents/DocumentsPage'),
  (module) => module.DocumentsPage,
);
const WorkAreasHomePage = lazyPage(
  () => import('@/features/work-areas/WorkAreasHomePage'),
  (module) => module.WorkAreasHomePage,
);
const WorkAreasActivityPage = lazyPage(
  () => import('@/features/work-areas/WorkAreasActivityPage'),
  (module) => module.WorkAreasActivityPage,
);
const WorkAreasViewsPage = lazyPage(
  () => import('@/features/work-areas/WorkAreasViewsPage'),
  (module) => module.WorkAreasViewsPage,
);
const SeededEmbeddedActivityPage = lazyPage(
  () => import('@/features/views/SeededEmbeddedActivityPage'),
  (module) => module.SeededEmbeddedActivityPage,
);
const SeededDashboardActivityPage = lazyPage(
  () => import('@/features/views/SeededDashboardActivityPage'),
  (module) => module.SeededDashboardActivityPage,
);
const SeededAgentUsagePage = lazyPage(
  () => import('@/features/views/SeededAgentUsagePanel'),
  (module) => module.SeededAgentUsagePage,
);
const SeededViewPreviewPage = lazyPage(
  () => import('@/features/views/SeededViewPage'),
  (module) => module.SeededViewPreviewPage,
);
const WorkAreaEditPage = lazyPage(
  () => import('@/features/work-areas/WorkAreaEditPage'),
  (module) => module.WorkAreaEditPage,
);
const AllWorkAreasSchedulesPage = lazyPage(
  () => import('@/features/schedules/AllWorkAreasSchedulesPage'),
  (module) => module.AllWorkAreasSchedulesPage,
);
const WorkAreaChoosePage = lazyPage(
  () => import('@/features/work-areas/WorkAreaChoosePage'),
  (module) => module.WorkAreaChoosePage,
);
const WorkAreasDocumentsPage = lazyPage(
  () => import('@/features/work-areas/WorkAreasDocumentsPage'),
  (module) => module.WorkAreasDocumentsPage,
);
const WorkAreasDecisionsPage = lazyPage(
  () => import('@/features/work-areas/WorkAreasDecisionsPage'),
  (module) => module.WorkAreasDecisionsPage,
);
const MyIdeasPage = lazyPage(
  () => import('@/features/ideas/MyIdeasPage'),
  (module) => module.MyIdeasPage,
);
const SharedWithYouPage = lazyPage(
  () => import('@/features/sharing/SharedWithYouPage'),
  (module) => module.SharedWithYouPage,
);
const SharedRequestPage = lazyPage(
  () => import('@/features/sharing/SharedRequestPage'),
  (module) => module.SharedRequestPage,
);
const WorkAreasTasksPage = lazyPage(
  () => import('@/features/work-areas/WorkAreasTasksPage'),
  (module) => module.WorkAreasTasksPage,
);
const AllWorkAreasItemsPage = lazyPage(
  () => import('@/features/work-areas/AllWorkAreasItemsPage'),
  (module) => module.AllWorkAreasItemsPage,
);
const AdminPage = lazyPage(
  () => import('@/features/admin/AdminPage'),
  (module) => module.AdminPage,
);
const ProfilePage = lazyPage(
  () => import('@/features/profile/ProfilePage'),
  (module) => module.ProfilePage,
);
const NewDecisionPage = lazyPage(
  () => import('@/features/decisions/DecisionFileForm'),
  (module) => module.NewDecisionPage,
);
const ReviseDecisionPage = lazyPage(
  () => import('@/features/decisions/DecisionFileForm'),
  (module) => module.ReviseDecisionPage,
);
const DecisionsWaitingPage = lazyPage(
  () => import('@/features/decisions/DecisionsWaitingPage'),
  (module) => module.DecisionsWaitingPage,
);
const TasksWaitingPage = lazyPage(
  () => import('@/features/tasks/TasksWaitingPage'),
  (module) => module.TasksWaitingPage,
);
// F-0321: a work area's Parts tree and a page per part (concept-parts §6.2).
const PartsTreePage = lazyPage(
  () => import('@/features/parts/PartsTreePage'),
  (module) => module.PartsTreePage,
);
// F-0424: a work area's Schedules, one schedule, its form (concept-scheduled-work §14).
const SchedulesPage = lazyPage(
  () => import('@/features/schedules/SchedulesPage'),
  (module) => module.SchedulesPage,
);
const SchedulePage = lazyPage(
  () => import('@/features/schedules/SchedulePage'),
  (module) => module.SchedulePage,
);
const ScheduleCreatePage = lazyPage(
  () => import('@/features/schedules/ScheduleForm'),
  (module) => module.ScheduleCreatePage,
);
const ScheduleEditPage = lazyPage(
  () => import('@/features/schedules/ScheduleForm'),
  (module) => module.ScheduleEditPage,
);
const PartPage = lazyPage(
  () => import('@/features/parts/PartPage'),
  (module) => module.PartPage,
);
const AgentsPage = lazyPage(
  () => import('@/features/agents/AgentsPage'),
  (module) => module.AgentsPage,
);
const ConversationsPage = lazyPage(
  () => import('@/features/conversations/ConversationsPage'),
  (module) => module.ConversationsPage,
);
const ConversationsPreviewPage = lazyPage(
  () => import('@/features/conversations/ConversationsPreviewPage'),
  (module) => module.ConversationsPreviewPage,
);

// F-0601 AMX-6a to AMX-6f (D-0331 (a)): Infrastructure and a machine's Permissions are the Agents
// explorer's machine node now; their addresses (the desktop menu's, Settings' relay link, a refused
// start's place, a card's Permissions link) only send the reader there.
const InfrastructureRedirect = lazyPage(
  () => import('@/features/infrastructure/InfrastructureRedirect'),
  (module) => module.InfrastructureRedirect,
);

function RouterRoot() {
  return (
    <UnsavedChangesProvider>
      <DesktopTitleBarProvider>
        {/* T-0320: the app shell's messages and events. Renders nothing, and nothing runs in a browser. */}
        <ShellRuntime />
        {/* F-0555: the Mac app asks once whether it may read its network address. Nothing in a browser. */}
        <MacAddressConsentPrompt />
        {/* I-1304: a tap on a link to a page of the app is the router's push, wherever it is drawn. */}
        <InternalLinkClicks />
        {/* I-1352 (IL-18): each history entry keeps the page it was pushed from, for its ‹ label. */}
        <HistoryEntries />
        {/* F-0361 (G2): the sheet a desktop entry opens — the sidebar's footer, the palette. */}
        <FeedbackSheetHost />
        {/*
          F-0160: the confirm-your-email banner draws nothing until a signed-in, unverified account
          exists — see the component's own header for why the root is the right place for it rather
          than `Layout.tsx`.

          AC-BANNER-3 to AC-BANNER-5: the three bars are in the entry, never chunks of their own. Each
          is drawn from the session in the first page's own commit, so none arrives after the page
          and pushes it down. A chunk the page waited for at most 1.5 s still came late on a loaded
          machine (39 px at 1280, 89 px at 390); the three cost 6.9 kB of the entry.
        */}
        <EmailVerificationBanner />
        {/* I-1395: a bounced or complained address is told on every screen, with the way out. */}
        <MailStandingBanner />
        {/* F-0224 §6: whoever signs in during an erasure's grace period is told on every screen. */}
        <PendingAccountDeletionBanner />
        <Outlet />
        {/*
          ⚠️ Drawn over every route, and it draws nothing unless the screen below it has no title bar
          of its own. Without it the desktop window cannot be moved on `/connect`, `/login`,
          `/register` or `/cli-auth` — the screens somebody sees before they have an account, which
          on a fresh installation is all of them.

          I-1423: last, not first. It is fixed, so its place in the document draws nothing, but first
          it took `:first-child` from the top bar below it (`index.css`, I-1101), and the bar was
          10 px taller until the tab bar claimed the title bar: the page moved up when it did.
        */}
        <DesktopTitleBarFallback />
      </DesktopTitleBarProvider>
    </UnsavedChangesProvider>
  );
}

/**
 * Every address this app answers, in the order React Router ranks them — and exactly the table it
 * routes on (R-5, F-0250):
 *
 * - the entry doors at the root, which never move (RA-22);
 * - every app route under `/-/`, declared in `appRoutes([...])` as it reads inside the app;
 * - every work area at `/<handle>[/<subgroup>…]/<slug>`, its routes after `/-/`, one route per depth;
 * - a namespace at `/<handle>`;
 * - the addresses from before the cutover, each replaced by its new one.
 *
 * Exported so the table itself can be asserted on (I-0449, RA-21, AC-AD2): which route a path lands on
 * is a fact about this list, and it is not visible from any one screen.
 */
const LAUNCH_DECISIONS_DATA: IBootDataHandle = {
  bootData: () => decisionApi.prefetchWaitingForMeEverywhere(),
};

export const ROUTES: RouteObject[] = [
  {
    element: <RouterRoot />,
    // I-0678: whatever a route throws is drawn by the app, never by React Router's developer screen.
    errorElement: <RouteLoadError />,
    children: [
      // F-0266 RL-1e, I-0703: every page before an account is drawn inside the public layout, which
      // draws its one main landmark, as `Layout` does for the app's pages.
      {
        element: <PublicLayout />,
        children: [
          // Eagerly loaded, not lazy: this is what an installation that can reach nothing falls back
          // to, and a chunk fetched from a deployment that is not answering would never arrive.
          { path: '/connect', element: <ConnectDeploymentPage /> },
          // F-0518: the desktop app's first screen — Create account and Sign in, nothing to type.
          // Eager for the same reason as `/connect`: it is drawn before any deployment has answered.
          { path: '/welcome', element: <WelcomePage /> },
          { path: '/login', element: <LoginPage /> },
          // F-0581 OB-1: a first start's way in, ahead of `/welcome` and `/login`.
          { path: '/get-started', element: <GetStartedPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/cli-auth', element: <CliAuthPage /> },
          { path: '/desktop-auth', element: <DesktopAuthPage /> },
          // F-0228: where the phone app's sign-in sheet opens. Public like `/cli-auth`, for the same reason.
          { path: '/mobile-auth', element: <MobileAuthPage /> },
          // I-0775: where the desktop app's passkey window opens, in the system web authentication
          // sheet. Public like `/mobile-auth`: the sheet shares no session with the app.
          { path: '/passkey-ceremony', element: <PasskeyCeremonyPage /> },
          // F-0237: where a setup link or QR code lands without the app. Public: it is read on a phone
          // that has never signed in here.
          { path: '/app-setup', element: <AppSetupPage /> },
          // F-0219: where `/oauth/authorize` sends a person to approve a cloud assistant. Public like
          // `/cli-auth` — the page itself sends a signed-out visitor to sign in and back.
          { path: '/connector-consent', element: <ConnectorConsentPage /> },
          // I-2219: where a local assistant's sign-in ends, back on this deployment once it holds its
          // token. Public: it reads nothing from the server, and says the account only when signed in.
          { path: appPath('/connected'), element: <ConnectedPage /> },
          // F-0616 S2: where the admin console's Sign in with FEATit lands (AC-AK47). Public like
          // `/connector-consent` — the page itself sends a signed-out visitor to sign in and back.
          { path: '/console-sign-in', element: <ConsoleSignInPage /> },
          { path: '/verify-session/:code', element: <VerifySessionPage /> },
          // F-0160: three public doors, none of them behind `ProtectedRoute` — a person confirming an
          // address or resetting a password by definition has no session yet on this device.
          { path: '/verify-email', element: <VerifyEmailPage /> },
          // R-13: an invitation link opens here signed in or not — the page itself asks for sign-in.
          { path: appPath('/invite'), element: <InvitePage /> },
          // F-0809 (EAI-1): a System Admin's invitation to the deployment itself.
          { path: appPath('/join'), element: <JoinPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
          // F-0160 §11 (I-0638): a new deployment's operator creates the first administrator here.
          { path: '/setup', element: <SetupPage /> },
          // F-0160 §11.5 (I-0638): where the person an agent set the deployment up for claims the account.
          { path: '/claim', element: <ClaimPage /> },
          // I-0637: where a non-transactional message's unsubscribe link lands in a browser.
          { path: '/unsubscribe', element: <UnsubscribePage /> },
          // F-0557: the two links a sign-in address change mails, opened from a mailbox with or
          // without a session. Each asks first and changes nothing on arrival.
          { path: '/confirm-sign-in-address', element: <ConfirmSignInAddressPage /> },
          { path: '/undo-sign-in-address', element: <UndoSignInAddressPage /> },
          // F-0266 RL-1e: a reporter's own page, opened by the link in a message; a reporter has no account.
          // Under `/-/` like `/-/invite`, so it can never shadow a namespace.
          { path: appPath('/reporter'), element: <ReporterPage /> },
          // UXA-11 (D-0349 a): a signed link to look at one version and approve it, `/-/review#token=…`;
          // its reader has no account.
          { path: appPath('/review'), element: <ReviewLinkPage /> },
          // I-2298 (PX-1): a customer moment's link, `/-/open#t=…`; the page asks a signed-out reader
          // to sign in and keeps the token in memory across it.
          { path: appPath('/open'), element: <CustomerLinkOpenPage /> },
          // F-0766 (D-0458 b): the documented lookup address, `/-/open/file?remote=…&path=…`; the page
          // asks a signed-out reader to sign in and back, then opens the file page in their work area.
          { path: appPath('/open/file'), element: <OpenRepositoryFilePage /> },
          {
            path: appPath('/artifact-invite'),
            element: (
              <ArtifactSharingAvailabilityBoundary>
                <ArtifactInvitationPage />
              </ArtifactSharingAvailabilityBoundary>
            ),
          },
          {
            path: appPath('/artifact-share-confirmations/:intentId'),
            element: (
              <ArtifactSharingAvailabilityBoundary>
                <ArtifactSnapshotConfirmationPage />
              </ArtifactSharingAvailabilityBoundary>
            ),
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: appPath('/artifact-shares/:grantId'),
            // I-2312: a focused page outside the app's layout, so its own frame draws its one main.
            element: (
              <PublicFrame>
                <ArtifactSharingAvailabilityBoundary>
                  <ArtifactRecipientPage />
                </ArtifactSharingAvailabilityBoundary>
              </PublicFrame>
            ),
          },
          {
            element: <Layout />,
            children: [
              {
                /*
                  I-0678: pathless, so every address below is where it was, and inside the layout, so
                  a screen that fails is drawn in the page area with the navigation still around it.
                */
                errorElement: <RouteLoadError />,
                children: [
                  // ADR-133: every old link to `/` lands on the home under `/-/`.
                  { path: '/', element: <RouteRedirect to={APP_HOME_PATH} /> },
                  ...appRoutes([
                    { path: '/new', element: <RouteRedirect to={appPath('/items/new')} /> },
                    { path: '/settings', element: <AdminPage /> },
                    // F-0241: where Stripe's Checkout and portal send the person back.
                    { path: '/settings/billing', element: <BillingReturnRedirect /> },
                    // F-0264 (CO-4.7): where GitLab sends a person back after they link their account.
                    {
                      path: '/settings/streams/account-callback',
                      element: <AccountCallbackPage />,
                    },
                    { path: '/admin', element: <AdminPage /> },
                    { path: '/profile', element: <ProfilePage /> },
                    // T-0320: the app's More tab — the web's navigation as a page.
                    { path: '/more', element: <MoreScreen /> },
                    // T-0318: the iPhone app's warm spare boots here and is routed on (`ShellSpareScreen`).
                    { path: '/shell-spare', element: <ShellSpareScreen /> },
                    { path: '/agents', element: <AgentsPage machineTabs /> },
                    // The Agents view with the seeded Token usage view in its usage panel's place: a
                    // parity screen only (F-0315). Nothing links here, and the Agents view keeps its
                    // panel until Jay decides the switch (ADR-062).
                    { path: '/agents/preview/usage', element: <SeededAgentUsagePage /> },
                    // F-0630 (D-0359 c): every agent conversation, and one opened beside the list.
                    { path: '/conversations', element: <ConversationsPage /> },
                    { path: '/conversations/:sessionKey', element: <ConversationsPage /> },
                    // Its seeded twin over the bridge's recorded fixtures, until S2 serves a machine's
                    // conversations. Nothing links here; a static segment outranks `:sessionKey`.
                    { path: '/conversations/preview', element: <ConversationsPreviewPage /> },
                    {
                      path: '/conversations/preview/:sessionKey',
                      element: <ConversationsPreviewPage />,
                    },
                    { path: '/infrastructure', element: <InfrastructureRedirect /> },
                    // F-0683 slice 9's page, folded into the machine node's Settings tab (AMX-6e).
                    {
                      path: '/infrastructure/machines/:machineId/permissions',
                      element: <InfrastructureRedirect />,
                    },
                    // F-0347: an organisation's (or subgroup's) Settings, with People and access as
                    // one section among several. `/organisations/:namespaceId` is I-0334's old
                    // address — kept working (OSE-3) and still landing on People, so a link written
                    // down before this shipped opens the same thing it always did.
                    {
                      path: '/organisations/:namespaceId',
                      element: <OrganisationSettingsPage legacyDefaultSection="people" />,
                    },
                    {
                      path: '/organisations/:namespaceId/settings',
                      element: <OrganisationSettingsPage />,
                    },
                    { path: '/work-areas', element: <WorkAreasHomePage /> },
                    { path: '/work-areas/activity', element: <WorkAreasActivityPage /> },
                    // T-0565: on a phone an item from that feed is a pushed screen, still in All.
                    ...activityItemRoutes('/work-areas/activity', <WorkAreasActivityPage />),
                    {
                      path: '/work-areas/views',
                      element: (
                        <ViewsIndexRoute
                          overview={<ViewsOverviewPage />}
                          editor={<WorkAreasViewsPage />}
                        />
                      ),
                    },
                    { path: '/work-areas/views/:viewId', element: <WorkAreasViewsPage /> },
                    // The seeded twin of `/work-areas/activity`, and the only screen in the product
                    // that renders a seed as an embedding. It exists so the parity ledger's
                    // `Activity, embedded` look-and-feel column has a second screen to be compared
                    // against instead of being graded from the code. Four segments, so it cannot be
                    // shadowed by the three-segment `:viewId` route above. Not a main view: nothing
                    // links here from a daily path, and no switch is implied by it.
                    {
                      path: '/work-areas/views/preview/embedded-activity',
                      element: <SeededEmbeddedActivityPage />,
                    },
                    // Every seed as a page, which is the shape a switch would actually ship. Listed after
                    // the static sibling above and matched after it too — React Router ranks a static
                    // segment over a dynamic one — and no seed is called `embedded-activity`.
                    {
                      path: '/work-areas/views/preview/:seedId',
                      element: <SeededViewPreviewPage />,
                    },
                    { path: '/work-areas/documents', element: <WorkAreasDocumentsPage /> },
                    // I-0560: *Decisions for me* keeps its navigation line when a reader widens to All.
                    {
                      path: '/work-areas/decisions',
                      element: <WorkAreasDecisionsPage />,
                      // T-0559: the app's launch tab; its list is asked for beside the session check.
                      handle: LAUNCH_DECISIONS_DATA,
                    },
                    /*
                I-0602: the selected decision is in the address here too, so selecting one keeps the
                reader in All work areas and the link to it is still shareable.

                ⛔ **The work area's address, then the key** (`…/decisions/acme/tracker/D-0003`) —
                not `/work-areas/decisions/:decisionKey`.
                A display key is project-local (`work_items` is `@Unique(['projectId', 'key'])`), so
                the key alone names no decision: any number of a reader's work areas may have a
                `D-0003` waiting for them, and the page would have to guess.
              */
                    ...workAreasDecisionRoutes(<WorkAreasDecisionsPage />),
                    // I-0900: "Tasks for me" under All work areas, directly below Decisions in the
                    // sidebar; I-0954: a task opens beside the list, addressed as a decision is.
                    // F-0547: Work items across every work area, the item selected in the query.
                    { path: '/work-areas/items', element: <AllWorkAreasItemsPage /> },
                    { path: '/work-areas/tasks', element: <WorkAreasTasksPage /> },
                    // U-0065: Schedules across every work area; a schedule opens in its own.
                    { path: '/work-areas/schedules', element: <AllWorkAreasSchedulesPage /> },
                    ...workAreasTaskRoutes(<WorkAreasTasksPage />),
                    /*
                      F-0383, §10.9: a namespace's own scope — the same views All work areas
                      offers, filtered to its subtree, on the same page components (the generic
                      view engine's own "no second implementation" rule, applied here). The bare
                      address lands on Home itself, the same page `/-/work-areas` is — not a
                      redirect to Activity, which a namespace-scoped URL used to be the only one of
                      the four routes below without a direct element for. The Views route's own
                      picker and catalog are filtered to the subtree inside `CustomViewsPage.tsx`
                      itself (landed once lane-webux's F-0363 merge did).
                    */
                    {
                      path: '/namespaces/:namespaceId',
                      element: <WorkAreasHomePage />,
                    },
                    {
                      path: `/namespaces/:namespaceId/${NAMESPACE_ACTIVITY_SEGMENT}`,
                      element: <WorkAreasActivityPage />,
                    },
                    // T-0566: on a phone an item from an organisation's feed is a pushed screen too.
                    ...activityItemRoutes(
                      `/namespaces/:namespaceId/${NAMESPACE_ACTIVITY_SEGMENT}`,
                      <WorkAreasActivityPage />,
                    ),
                    {
                      path: `/namespaces/:namespaceId/${NAMESPACE_DECISIONS_SEGMENT}`,
                      element: <WorkAreasDecisionsPage />,
                    },
                    // NV-6's own sub-selection: a decision opened from inside this namespace's
                    // list keeps the namespace in the address (`namespaceDecisionPath`), the same
                    // way `workAreasDecisionRoutes` does it for All work areas (I-0602).
                    ...namespaceDecisionRoutes(<WorkAreasDecisionsPage />),
                    {
                      path: `/namespaces/:namespaceId/${NAMESPACE_ITEMS_SEGMENT}`,
                      element: <AllWorkAreasItemsPage />,
                    },
                    {
                      path: `/namespaces/:namespaceId/${NAMESPACE_TASKS_SEGMENT}`,
                      element: <WorkAreasTasksPage />,
                    },
                    ...namespaceTaskRoutes(<WorkAreasTasksPage />),
                    {
                      path: `/namespaces/:namespaceId/${NAMESPACE_VIEWS_SEGMENT}`,
                      element: (
                        <ViewsIndexRoute
                          overview={<ViewsOverviewPage />}
                          editor={<WorkAreasViewsPage />}
                        />
                      ),
                    },
                    // NV-6: a saved view opened inside the namespace keeps the namespace in its
                    // address, as `/work-areas/views/:viewId` does for All work areas.
                    {
                      path: `/namespaces/:namespaceId/${NAMESPACE_VIEWS_SEGMENT}/:viewId`,
                      element: <WorkAreasViewsPage />,
                    },
                    // F-0271 RL-5e: what is shared with this reader as a guest, in every work area.
                    { path: '/shared', element: <SharedWithYouPage /> },
                    // I-2447: one shared request on its own, full width ("See your request").
                    { path: '/shared/:workAreaId/:itemKey', element: <SharedRequestPage /> },
                    // F-0461 (OF-31): the ideas the reader reported, in every work area.
                    { path: '/my-ideas', element: <MyIdeasPage /> },
                    // I-0405: a view that needs one work area asks which, instead of redirecting.
                    { path: '/work-areas/choose/:view', element: <WorkAreaChoosePage /> },
                    // F-0530 (AC-19.4): the native switcher's Edit view, the picker's edit mode.
                    { path: '/work-areas/edit', element: <WorkAreaEditPage /> },
                    { path: '/home', element: <DashboardPage /> },
                    { path: '/roadmap', element: <RoadmapPage /> },
                    { path: '/activity', element: <ActivityPage /> },
                    { path: '/views/:viewId', element: <CustomViewsPage /> },
                    {
                      path: '/views',
                      element: (
                        <ViewsIndexRoute
                          overview={<ViewsOverviewPage />}
                          editor={<CustomViewsPage />}
                        />
                      ),
                    },
                    { path: '/documents/:documentId', element: <DocumentsPage /> },
                    { path: '/documents', element: <DocumentsPage /> },
                    { path: '/items/new', element: <WorkItemCreatePage /> },
                    { path: '/items/:key', element: <WorkItemsPage /> },
                    { path: '/items', element: <WorkItemsPage /> },
                    // I-0449: the unscoped twin of a work area's decisions, reading the selected work area
                    // like `/items` and `/roadmap`.
                    { path: '/decisions', element: <DecisionsWaitingPage /> },
                    // The unscoped twin of a work area's decision (I-0474), reading the selected work area
                    // exactly as `/decisions` does.
                    { path: '/decisions/:decisionKey', element: <DecisionsWaitingPage /> },
                    // I-0900: the unscoped twin of a work area's tasks, reading the selected work
                    // area like `/decisions` and `/items` do.
                    { path: '/tasks', element: <TasksWaitingPage /> },
                    { path: '/tasks/:itemKey', element: <TasksWaitingPage /> },
                    /*
                    AC-AD12: a feedback entry's detail. It was the router's one-segment catch-all,
                    `/:id`, and under R-5 a single segment is a namespace.
                  */
                    { path: '/feedback/:id', element: <FeedbackDetailRoute /> },
                  ]),
                  /*
                    R-5: a work area at `/<handle>[/<subgroup>…]/<slug>`, and its routes after `/-/`
                    (`/acme/tracker/-/items/F-0160`). One route per namespace depth; the children read
                    as they do inside the work area.
                  */
                  ...workAreaAddressRoutes(<WorkAreaRouteBoundary />, [
                    { index: true, element: <DashboardPage /> },
                    { path: 'pages/:dashboardPageId', element: <DashboardPage /> },
                    {
                      path: 'pages/:dashboardPageId/items/:scopeItemKey',
                      element: <ItemScopedDashboardPage />,
                    },
                    { path: 'settings', element: <WorkAreaSettingsRedirect /> },
                    { path: 'roadmap', element: <RoadmapPage /> },
                    { path: 'activity', element: <ActivityPage /> },
                    // IU-12b (F-0540): an item pushed from the work area's Activity on a phone.
                    { path: 'activity/:itemKey', element: <WorkAreaActivityItemScreen /> },
                    // F-0564: a commit's changed files and diff, under the work area's `/-/`.
                    { path: 'commits/:sha', element: <CommitDiffPage /> },
                    // U-0023: a repository file named in an item's text, read on the reader's machine.
                    ...repositoryFileRoutePaths().map((path) => ({
                      path,
                      element: <RepositoryFilePage />,
                    })),
                    { path: 'views/:viewId', element: <CustomViewsPage /> },
                    {
                      path: 'views',
                      element: (
                        <ViewsIndexRoute
                          overview={<ViewsOverviewPage />}
                          editor={<CustomViewsPage />}
                        />
                      ),
                    },
                    // The seeded twin of this work area's dashboard activity block: the dashboard
                    // with the seeded Activity view in the block's place, so the parity ledger can
                    // measure the block beside `/<work area>` (I-0237, AEM-3). A parity screen only:
                    // nothing links here, and no main view changes hands with it.
                    {
                      path: 'views/preview/dashboard-activity',
                      element: <SeededDashboardActivityPage />,
                    },
                    { path: 'documents/:documentId', element: <DocumentsPage /> },
                    { path: 'documents', element: <DocumentsPage /> },
                    { path: 'decisions', element: <DecisionsWaitingPage /> },
                    { path: 'decisions/new', element: <NewDecisionPage /> },
                    /*
                    I-0474: the selected decision is in the address, so a link to one is shareable and
                    the back button does what it looks like it does — while the list stays mounted
                    around it. Declared after `decisions/new`, though the order does not decide it:
                    React Router ranks a static segment above a dynamic one, so `/decisions/new`
                    still files a decision rather than opening one named "new".
                  */
                    { path: 'decisions/:decisionKey', element: <DecisionsWaitingPage /> },
                    // I-0484: reword an unanswered decision in place, on the same form that files one.
                    { path: 'decisions/:decisionKey/revise', element: <ReviseDecisionPage /> },
                    // I-0900: "Tasks for me", directly below Decisions in the sidebar.
                    { path: 'tasks', element: <TasksWaitingPage /> },
                    // I-0954: a task opens beside the list, as `decisions/:decisionKey` does.
                    { path: 'tasks/:itemKey', element: <TasksWaitingPage /> },
                    // T-0492 (TF-22): the Holds page is folded into Tasks for me; its old addresses
                    // land there, with the item selected.
                    { path: 'holds', element: <HoldsAddressRedirect /> },
                    { path: 'holds/:itemKey', element: <HoldsAddressRedirect /> },
                    // F-0321: scoped only — a part tree belongs to its leading work area (D-0206).
                    // A part's handle is one segment, its slashes encoded (`partUrls.ts`).
                    { path: 'parts', element: <PartsTreePage /> },
                    { path: 'parts/:handle', element: <PartPage /> },
                    // F-0424: scoped only, like parts — a schedule belongs to its work area. `new` is a
                    // reserved slug (`SCHEDULE_RESERVED_SLUGS`), so it never names a schedule.
                    { path: 'schedules', element: <SchedulesPage /> },
                    { path: 'schedules/new', element: <ScheduleCreatePage /> },
                    { path: 'schedules/:scheduleSlug', element: <SchedulePage /> },
                    { path: 'schedules/:scheduleSlug/edit', element: <ScheduleEditPage /> },
                    { path: 'schedules/:scheduleSlug/runs/:runId', element: <SchedulePage /> },
                    { path: 'items/new', element: <WorkItemCreatePage /> },
                    { path: 'items/:key', element: <WorkItemsPage /> },
                    { path: 'items', element: <WorkItemsPage /> },
                  ]),
                  // AC-AD12: a namespace, `/acme`. The first level is always the handle.
                  { path: '/:namespace', element: <NamespaceAddressPage /> },
                  // AC-AD4 … AC-AD6: an address from before R-5, replaced by its new one.
                  ...legacyAddressRoutes(<LegacyAddressRedirect />),
                  /*
                    F-0250: an address no route above takes — deeper than any namespace may go, or a
                    route longer than any declared — gets the not-found state its shape calls for,
                    never the crash screen. Relative, so it is not a root route (AC-AD2); the app's
                    path configuration answers it with its fallback rule.
                  */
                  {
                    path: '*',
                    element: <WorkAreaRouteBoundary />,
                    handle: WORK_AREA_BOUNDARY_HANDLE,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

/**
 * T-0559: what a cold load of `pathname` can start before anything has rendered. Called once at
 * boot; nothing is fetched until the caller starts it.
 *
 * - `startCode`: the code of every lazy route the path lands on, so the launch screen's chunk
 *   arrives while the session is still being checked rather than after it. Drawn once it is in, the
 *   route never shows "Loading page", and React's 300 ms hold on a revealed fallback is never paid.
 *   `ProtectedRoute` keeps its spinner until that code is in (`bootRoutes.ts`). Under the shell, the
 *   sidebar's view tree is fetched too, held for by nothing.
 * - `checksSession`: whether the path lands behind `ProtectedRoute`, whose first act is the session
 *   check: the caller starts it then, without waiting for React to render down to the route's effect.
 * - `startData`: the launch screen's first read, beside the session check.
 *
 * I-1380: the caller sends the session check and the first read before it starts the code. A
 * launch asks for some forty chunks at once, and over HTTP/1.1 a browser opens six connections to
 * one host, so a check asked for after them waited for the chunks ahead of it (490 ms on an
 * iPhone's warm launch, T-0559 run 3).
 */
export function bootRoutesFor(
  pathname: string,
  routes: RouteObject[] = ROUTES,
): { checksSession: boolean; startCode: () => void; startData: () => void } {
  let checksSession = false;
  const code: (() => Promise<void>)[] = [];
  let sidebarViews = false;
  const data: (() => void)[] = [];
  for (const match of bootMatches(pathname, routes)) {
    const type = (match.route.element as ReactElement | undefined)?.type as
      | { preload?: () => Promise<void> }
      | undefined;
    if (type === ProtectedRoute) checksSession = true;
    if (type?.preload) code.push(type.preload.bind(type));
    if (type === Layout) sidebarViews = true;
    const bootData = bootDataOf(match.route.handle);
    if (bootData) data.push(bootData);
  }
  return {
    checksSession,
    startCode: () => {
      const fetches = code.map((preload) => preload());
      if (sidebarViews) preloadSidebarViews();
      holdForBootRoutes(fetches);
    },
    startData: () => data.forEach((start) => start()),
  };
}

const router = createBrowserRouter(ROUTES);

/**
 * T-0559: the router matched the address it was made at, as this module ran, so the boot reads its
 * matches rather than matching the whole tree a second time: `matchRoutes` flattens and ranks every
 * route on each call, about 20 ms on a 4× CPU, and the boot's call ran before the session check could
 * go out. Any other address, or another tree (a test's), is matched here.
 */
function bootMatches(pathname: string, routes: RouteObject[]): { route: RouteObject }[] {
  if (routes === ROUTES && pathname === router.state.location.pathname) return router.state.matches;
  return matchRoutes(routes, pathname) ?? [];
}

/** Where a new deployment is taken: the reader's next move, never the screen they are reading. */
const onReaderNavigation = (onNavigate: () => void) =>
  subscribeToReaderNavigation(router, onNavigate);

function App() {
  const pendingDeployment = useDeploymentReload({ subscribeToNavigation: onReaderNavigation });
  // Runs for as long as the app does, not for as long as a view is open — see the hook.
  useMachineReporter();
  useBrowserWatchReporter();
  // I-1078: a featit-return link (desktop OAuth sign-in or Stripe checkout/portal) routes into
  // this same router, client-side, the way an in-app link already does.
  useDesktopReturnLinks(router);
  // F-0572: the menu-bar menu's *Relay status…* routes into this router too.
  useDesktopMenuRoutes(router);
  // IU-1 (S-1): the app's shell, in phone mode, wants the document itself to be the one scroller.
  // See `useSinglePageScroller` and `AppViewport`'s own doc for the rest of the mechanism.
  const documentScrolls = useSinglePageScroller();
  // F-0510 IU-10: the tap that opens the create sheet raises the keyboard for its title, in the app.
  useEffect(() => installCreateKeyboardPrime(), []);

  return (
    <ThemeProvider>
      {/*
        I-0580: `AppViewport` owns the window's height, and every top bar is a row inside it.

        ⛔ Do not mount a bar outside this element. `MaintenanceNotice` used to sit where
        `AppViewport` now does — above a `Layout` that was itself a full viewport — so the two
        stacked and the shell's bottom, sidebar footer and all, was pushed off the screen. Inside
        here a bar reduces the content by existing; outside it, it adds to the document again.
        `top-bar-shrinks-content.spec.ts` fails and names any bar that ends up outside.

        `StoreProvider` and the router render no box of their own, so the bars and the routes below
        them are all flex rows of this one column, whichever depth they are written at.

        I-2377: `MaintenanceNotice` is mounted here but drawn at the bottom: it is a bottom bar
        (`ViewportBottomBar`), which renders into the shell's slot on the tab bar, or into this
        viewport's own last row on the screens without the shell.
      */}
      <AppViewport documentScrolls={documentScrolls}>
        <UngatedPreviewBar />
        <MaintenanceNotice />
        <StoreProvider>
          {/* Drawn by the slots the shell reserves for it, never over the page (I-0781, QA 267). */}
          <DeploymentUpdateProvider update={pendingDeployment}>
            <RouterProvider router={router} />
          </DeploymentUpdateProvider>
        </StoreProvider>
        {/* F-0324: a refused control's reason, on a tap, for a touch screen with no tooltip. */}
        <RefusedTouchHint />
        {/* F-0324 AC-DH34: the reason each refused control names as its description, for a screen reader. */}
        <RefusalReasons />
        {/* F-0578 WAIT-14: a gated control still checking access shows the session's study. */}
        <GatePendingStudiesOnDemand />
      </AppViewport>
    </ThemeProvider>
  );
}

export default App;
